import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth, generateApiKey } from './AuthContext';

const BotContext = createContext(null);

const DEFAULT_BOT = {
  name: 'Aria',
  companyName: '',
  companyDesc: '',
  knowledgeBase: '',
  tone: 'friendly and professional',
  accentColor: '#f59e0b',
  escalationEmail: '',
  escalationKeywords: ['refund', 'cancel', 'angry', 'legal', 'fraud', 'broken'],
  quickReplies: ['What are your pricing plans?', 'How do I get started?', 'Contact support'],
  widgetPosition: 'right',
  active: true,
  conversations: 0,
  resolved: 0,
  escalated: 0,
  satisfaction: 4.2,
  riskScore: 'green',
  revenue: 0,
  playbooks: [],
  abTests: [],
  integrations: { hubspot: false, shopify: false, zendesk: false, gmail: false, stripe: false },
  files: [],
  logs: [],
};

export const BotProvider = ({ children }) => {
   const { user } = useAuth();
  const [bots, setBots] = useState([]);

  useEffect(() => {
    if (!user) { setBots([]); return; }
    const stored = localStorage.getItem(`retflow_bots_${user.id}`);
    setBots(stored ? JSON.parse(stored) : []);
  }, [user]);

  const saveBots = (newBots) => {
    setBots(newBots);
    if (user) localStorage.setItem(`retflow_bots_${user.id}`, JSON.stringify(newBots));
  };

  const createBot = (overrides = {}) => {
    const bot = {
      ...DEFAULT_BOT,
      ...overrides,
      id: `bot_${Date.now()}`,
      apiKey: generateApiKey(),
      createdAt: new Date().toISOString(),
      // Seed with demo analytics data
      conversations: Math.floor(Math.random()*400)+100,
      resolved: Math.floor(Math.random()*300)+80,
      escalated: Math.floor(Math.random()*30)+5,
      satisfaction: (Math.random()*1.5+3.2).toFixed(1),
      revenue: Math.floor(Math.random()*8000)+2000,
      logs: generateDemoLogs(),
    };
    const updated = [...bots, bot];
    saveBots(updated);
    return bot;
  };

  const updateBot = (id, changes) => {
    const updated = bots.map(b => b.id === id ? { ...b, ...changes } : b);
    saveBots(updated);
  };

  const deleteBot = (id) => {
    saveBots(bots.filter(b => b.id !== id));
  };

  const regenerateApiKey = (id) => {
    updateBot(id, { apiKey: generateApiKey() });
  };

  const addPlaybook = (botId, playbook) => {
    const bot = bots.find(b => b.id === botId);
    if (!bot) return;
    const updated = [...(bot.playbooks || []), { ...playbook, id: `pb_${Date.now()}`, active: true }];
    updateBot(botId, { playbooks: updated });
  };

  const addFile = (botId, file) => {
    const bot = bots.find(b => b.id === botId);
    if (!bot) return;
    const updated = [...(bot.files || []), file];
    updateBot(botId, { files: updated });
  };

  return (
    <BotContext.Provider value={{ bots, createBot, updateBot, deleteBot, regenerateApiKey, addPlaybook, addFile }}>
      {children}
    </BotContext.Provider>
  );
};

export const useBots = () => useContext(BotContext);

function generateDemoLogs() {
  const topics = ['Pricing question', 'Return policy', 'Shipping time', 'Account issue', 'Feature request', 'Billing query', 'Integration help'];
  const sentiments = ['positive', 'positive', 'neutral', 'positive', 'negative', 'neutral', 'positive'];
  return Array.from({length:12}, (_, i) => ({
    id: `log_${i}`,
    topic: topics[i % topics.length],
    sentiment: sentiments[i % sentiments.length],
    resolved: Math.random() > 0.2,
    escalated: Math.random() < 0.1,
    revenue: Math.random() > 0.6 ? Math.floor(Math.random()*500)+50 : 0,
    time: new Date(Date.now() - i * 3600000 * Math.random()*4).toISOString(),
    riskScore: ['green','green','yellow','green','red','green','yellow'][i % 7],
  }));
}
